import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import { of } from 'rxjs';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { DateTime } from 'luxon';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Option } from '@app/forms/formly/formly-utils';
import { InputComponent } from '@voyagu/portal-design-system';

@Component({
  selector: 'date-field',
  templateUrl: './date-field.component.html',
  styleUrls: ['./date-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateFieldComponent extends FieldType<FieldTypeConfig> implements OnInit {
  months = of(getMonthsOptions());

  month: Option['value'] = '';
  day = '';
  year = '';

  touchedMonth: boolean;
  touchedDay: boolean;
  touchedYear: boolean;
  isFocusWithin = false;

  writingValue: boolean;
  commitTouchedTimeout: ReturnType<typeof setTimeout>;

  @ViewChild('dayInput') dayInput: InputComponent;
  @ViewChild('yearInput') yearInput: InputComponent;

  constructor(
    private elementRef: ElementRef,
    private changeDetectorRef: ChangeDetectorRef,
    private dr: DestroyRef,
  ) {
    super();
  }

  get showFieldErrors(): boolean {
    return !!this.formControl.touched && !!this.formControl.errors && !this.isFocusWithin;
  }

  @HostListener('focusin')
  onFocusIn() {
    this.isFocusWithin = true;
    this.changeDetectorRef.markForCheck();
  }

  @HostListener('focusout')
  onFocusOut() {
    this.scheduleCommitTouched();
  }

  @HostListener('document:click', ['$event']) click(event: MouseEvent) {
    const isBlured = !event.composedPath().includes(this.elementRef?.nativeElement as HTMLElement);
    const hasChanges = this.touchedMonth || this.touchedDay || this.touchedYear;

    if (isBlured && hasChanges) {
      this.commitTouchedState();
    }
  }

  ngOnInit() {
    this.formControl.valueChanges.pipe(takeUntilDestroyed(this.dr)).subscribe((value) => {
      if (this.writingValue) return;

      this.applyValueToParts(value as string | undefined | null);
      this.changeDetectorRef.detectChanges();
    });

    this.applyValueToParts(this.formControl.value as string | undefined | null);

    if (this.formControl.value) {
      this.formControl.updateValueAndValidity({ emitEvent: false });
    }

    this.dr.onDestroy(() => clearTimeout(this.commitTouchedTimeout));
  }

  setMonth(month: Option['value']) {
    this.month = month;
    this.touchedMonth = true;
    this.setFullDate();
  }

  onDayChange(value: string) {
    this.day = sanitizeDigits(value, 2);
    this.setFullDate();
  }

  onYearChange(value: string) {
    this.year = sanitizeDigits(value, 4);
    this.setFullDate();
  }

  onDayKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.yearInput?.focus();
    }
  }

  onYearKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.focusNextControl();
    }
  }

  onDayBlur() {
    this.touchedDay = true;
    this.setFullDate();
    this.scheduleCommitTouched();
  }

  onYearBlur() {
    this.touchedYear = true;
    this.setFullDate();
    this.scheduleCommitTouched();
  }

  onMonthTouched() {
    this.touchedMonth = true;
    this.scheduleCommitTouched();
  }

  setFullDate() {
    const month = (this.month as string) || '';
    const day = this.day;
    const year = this.year;

    this.writingValue = true;

    if (!month && !day && !year) {
      this.formControl.setValue(null);
      this.formControl.setErrors(this.props.required ? { required: true } : null);
      this.writingValue = false;
      return;
    }

    if (!month || !day || !year || year.length !== 4) {
      this.formControl.setValue(null);
      this.formControl.setErrors({ invalid: true });
      this.writingValue = false;
      return;
    }

    const paddedDay = day.padStart(2, '0');
    const parsedDate = DateTime.fromFormat(`${year}-${month}-${paddedDay}`, 'yyyy-MM-dd');

    if (!parsedDate.isValid) {
      this.formControl.setValue(null);
      this.formControl.setErrors({ invalid: true });
      this.writingValue = false;
      return;
    }

    if (this.props['disallowFuture'] && parsedDate > DateTime.now().startOf('day')) {
      this.formControl.setValue(null);
      this.formControl.setErrors({ invalid: true });
      this.writingValue = false;
      return;
    }

    this.formControl.setErrors(null);
    this.formControl.setValue(parsedDate.toISODate());
    this.writingValue = false;
  }

  scheduleCommitTouched() {
    clearTimeout(this.commitTouchedTimeout);
    this.commitTouchedTimeout = setTimeout(() => {
      const root = this.elementRef.nativeElement as HTMLElement;
      this.isFocusWithin = root.contains(document.activeElement);

      if (!this.isFocusWithin) {
        this.commitTouchedState();
      }

      this.changeDetectorRef.markForCheck();
    });
  }

  commitTouchedState() {
    if (this.touchedMonth && this.touchedDay && this.touchedYear) {
      this.formControl.markAsTouched();
    } else if (this.touchedDay || this.touchedYear) {
      const leavingIncomplete = !this.month || !this.day || !this.year || this.year.length !== 4;
      if (leavingIncomplete) {
        this.formControl.markAsTouched();
      }
    }

    this.changeDetectorRef.markForCheck();
  }

  applyValueToParts(value: string | undefined | null) {
    if (value) {
      const parsedDate = DateTime.fromFormat(value, 'yyyy-MM-dd');

      if (parsedDate.isValid) {
        this.month = parsedDate.get('month').toString().padStart(2, '0');
        this.day = parsedDate.get('day').toString();
        this.year = parsedDate.get('year').toString();
        return;
      }
    }

    this.month = '';
    this.day = '';
    this.year = '';
  }

  focusNextControl() {
    const card = (this.elementRef.nativeElement as HTMLElement)?.closest(
      '.traveler-card',
    ) as HTMLElement | null;
    const root = card || document;
    const selector =
      (this.props['nextFocusSelector'] as string | undefined) || '[data-id="gender"]';
    const host = root.querySelector(selector) as HTMLElement | null;
    const focusable =
      (host?.matches('select, input, textarea, button')
        ? host
        : (host?.querySelector('select, input, textarea, button') as HTMLElement | null)) || null;

    focusable?.focus();
  }
}

export const getMonthsOptions = () => {
  return [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ].map((month, i) => {
    const index = (i + 1).toString().padStart(2, '0');

    return {
      value: index,
      label: month,
    } as Option;
  });
};

const sanitizeDigits = (value: string, maxLength: number) => {
  return value.replace(/\D/g, '').slice(0, maxLength);
};
